import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Skeleton,
} from "@repo/ui";
import { Link } from "@tanstack/react-router";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { ArrowRight, Package } from "lucide-react";

interface InventoryStatus {
  in_stock: number;
  reorder_needed: number;
  sold_out: number;
}

interface DashboardInventoryHealthProps {
  status: InventoryStatus | null;
  totalItems: number | null;
  isLoading: boolean;
}

const STATUS_CONFIG = [
  {
    key: "in_stock" as const,
    label: "In Stock",
    color: "#15803D", // success token
    dotColor: "bg-success",
  },
  {
    key: "reorder_needed" as const,
    label: "Reorder",
    color: "#D97706", // warning token
    dotColor: "bg-warning",
  },
  {
    key: "sold_out" as const,
    label: "Sold Out",
    color: "#EF4444", // destructive token
    dotColor: "bg-destructive",
  },
];

function MiniTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; payload: { fill: string } }>;
}) {
  if (!active || !payload?.[0]) return null;
  const entry = payload[0];

  return (
    <div className="rounded-lg border border-border bg-surface p-2 shadow-lg">
      <div className="flex items-center gap-2">
        <div
          className="h-2 w-2 rounded-full"
          style={{ backgroundColor: entry.payload.fill }}
        />
        <span className="text-xs text-muted-foreground">{entry.name}</span>
        <span className="text-xs font-semibold tabular-nums">
          {entry.value}
        </span>
      </div>
    </div>
  );
}

function HealthSkeleton() {
  return (
    <div className="flex items-center gap-4">
      <Skeleton className="h-20 w-20 rounded-full shrink-0" />
      <div className="flex-1 space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="flex items-center justify-between">
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-4 w-8" />
          </div>
        ))}
      </div>
    </div>
  );
}

export function DashboardInventoryHealth({
  status,
  totalItems,
  isLoading,
}: DashboardInventoryHealthProps) {
  const total = totalItems ?? 0;
  const hasItems = total > 0;

  const pieData = status
    ? STATUS_CONFIG.map((config) => ({
        name: config.label,
        value: status[config.key],
        fill: config.color,
      }))
    : [];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-sm font-semibold">
          Inventory Health
        </CardTitle>
        <Link to="/inventory">
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-foreground gap-1"
          >
            Details
            <ArrowRight className="h-3.5 w-3.5" />
          </Button>
        </Link>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <HealthSkeleton />
        ) : !hasItems ? (
          <div className="flex flex-col items-center justify-center py-6 text-center">
            <div className="rounded-full bg-muted p-2.5 mb-2">
              <Package className="h-5 w-5 text-muted-foreground" />
            </div>
            <p className="text-xs text-muted-foreground">
              No inventory items yet
            </p>
          </div>
        ) : (
          <div className="flex items-center gap-4">
            {/* Mini donut */}
            <div className="relative shrink-0">
              <ResponsiveContainer width={80} height={80}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={24}
                    outerRadius={38}
                    paddingAngle={2}
                    dataKey="value"
                    strokeWidth={0}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={index} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip content={<MiniTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <p className="text-sm font-semibold tabular-nums leading-none">
                    {total}
                  </p>
                </div>
              </div>
            </div>

            {/* Status rows */}
            <div className="flex-1 space-y-2.5">
              {STATUS_CONFIG.map((config) => {
                const count = status?.[config.key] ?? 0;
                return (
                  <div
                    key={config.key}
                    className="flex items-center justify-between"
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className={`h-2 w-2 rounded-full ${config.dotColor}`}
                      />
                      <span className="text-xs text-muted-foreground">
                        {config.label}
                      </span>
                    </div>
                    <span className="text-sm font-medium tabular-nums">
                      {count}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
